(function () {
  'use strict';

  var VERSION = '1.0.0';
  var STORAGE_KEY = 'dc_postcode';
  var CHANGE_EVENT = 'cozy:delivery-change';
  var activeController = null;

  function parseJson(root, selector, fallback) {
    var element = root.querySelector(selector);
    try {
      return JSON.parse(element ? element.textContent : '');
    } catch (error) {
      return fallback;
    }
  }

  function createController(popup) {
    var sectionId = popup.dataset.dcSectionId;
    if (!sectionId) return null;

    document.body.appendChild(popup);

    var listeners = new AbortController();
    var triggerBtns = document.querySelectorAll('[data-dc-trigger="' + sectionId + '"]');
    var input = popup.querySelector('.dc-popup__input');
    var checkBtn = popup.querySelector('.dc-popup__check-btn');
    var resultsEl = popup.querySelector('.dc-results');
    var closeBtn = popup.querySelector('.dc-popup__close');
    var overlay = popup.querySelector('.dc-popup__overlay');
    var dcI18n = parseJson(popup, '[data-dc-i18n]', {});
    var zones = parseJson(popup, '[data-dc-zone-config]', []);
    var postcodeZones = Object.create(null);
    var selectedPostcode = '';
    var invalidResult = false;
    var lastRenderKey = '';
    var lastPublishedKey = '';
    var timer;
    var controller;
    var clock = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Australia/Sydney',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });

    function parseTime(value) {
      var match = String(value || '').match(/^([01]\d|2[0-3]):([0-5]\d)$/);
      return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 : null;
    }

    zones.forEach(function (zone) {
      // Older metaobjects omit this field and retain their same-day rules.
      // Fixed next-day delivery is explicit; a zone name is never a scheduling rule.
      zone.deliveryType = zone.deliveryType || 'same_day';
      zone.nextDay = zone.deliveryType === 'next_day';
      zone.start = parseTime(zone.startTime);
      zone.cutoff = parseTime(zone.cutoffTime);
      zone.min = Number(zone.minimumOrder);
      zone.fee = zone.deliveryFee == null || zone.deliveryFee === '' ? null : Number(zone.deliveryFee);
      if (!Number.isFinite(zone.fee) || zone.fee < 0) zone.fee = null;
      zone.free = !zone.nextDay && zone.freeShipping ? Number(zone.freeShippingThreshold) : null;
      zone.fast = !zone.nextDay && Boolean(zone.fastDelivery);

      if (
        !zone.enabled ||
        !['same_day', 'next_day'].includes(zone.deliveryType) ||
        (!zone.nextDay && (zone.start === null || zone.cutoff === null || zone.start >= zone.cutoff)) ||
        !(zone.min > 0) ||
        (zone.free !== null && !(zone.free > 0))
      ) {
        return;
      }

      String(zone.postcodes || '')
        .replace(/[，、;]/g, ',')
        .split(/[,\r\n]+/)
        .forEach(function (item) {
          var match = item.trim().match(/^(\d{4})(?:\s*-\s*.+)?$/);
          if (!match) return;
          var postcode = match[1];
          postcodeZones[postcode] =
            postcode in postcodeZones && postcodeZones[postcode] !== zone ? null : zone;
        });
    });

    function listen(target, event, handler, capture) {
      if (target) target.addEventListener(event, handler, { signal: listeners.signal, capture: Boolean(capture) });
    }

    function escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, function (character) {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }[character];
      });
    }

    function sydneySeconds() {
      var parts = {};
      clock.formatToParts(new Date()).forEach(function (part) {
        parts[part.type] = part.value;
      });
      return Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    }

    function currentState() {
      var zone = postcodeZones[selectedPostcode];
      var rule = zone || null;
      var seconds = sydneySeconds();
      var period = rule
        ? rule.nextDay ? 'always' : seconds >= rule.cutoff
          ? 'after'
          : seconds < rule.start
          ? 'before'
          : 'open'
        : '';

      return {
        zone: zone ? zone.name : '',
        ruleId: zone ? zone.id : '',
        rule: rule,
        period: period,
        tier: !selectedPostcode ? 'unknown' : !rule ? 'none' : rule.nextDay || period === 'after' ? 'next-day' : 'same-day',
        remaining: rule && !rule.nextDay && period !== 'after' ? rule.cutoff - seconds : 0
      };
    }

    function publicState(state) {
      return {
        postcode: selectedPostcode,
        zone: state.zone,
        zoneId: state.ruleId,
        tier: state.tier,
        period: state.period,
        deliveryType: state.rule ? state.rule.deliveryType : null,
        minimumOrder: state.rule ? state.rule.min : null,
        deliveryFee: state.rule ? state.rule.fee : null,
        freeShippingThreshold: state.rule ? state.rule.free : null,
        deliveryStartTime: state.rule && !state.rule.nextDay ? state.rule.startTime : null,
        cutoffTime: state.rule && !state.rule.nextDay ? state.rule.cutoffTime : null,
        fastDelivery: state.rule ? state.rule.fast : false,
        remainingSeconds: state.remaining
      };
    }

    function publish(state) {
      var detail = publicState(state);
      var key = [
        detail.postcode,
        detail.zoneId,
        detail.tier,
        detail.period,
        detail.deliveryType,
        detail.minimumOrder,
        detail.deliveryFee,
        detail.freeShippingThreshold,
        detail.deliveryStartTime,
        detail.cutoffTime,
        detail.fastDelivery
      ].join(':');

      if (key === lastPublishedKey) return;
      lastPublishedKey = key;
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: detail }));
    }

    function configuredStartText(rule, tomorrow) {
      if (rule.start === 36000) return tomorrow ? dcI18n.headerTomorrowAt10 : dcI18n.headerStartAt10;
      if (rule.start === 39600) return tomorrow ? dcI18n.headerTomorrowAt11 : dcI18n.headerStartAt11;
      var template = tomorrow ? dcI18n.headerTomorrowAtTime : dcI18n.headerStartAtTime;
      return template.replace(/%%time%%/g, rule.startTime);
    }

    function headerText(state) {
      if (state.tier === 'unknown') return dcI18n.headerChooseArea;
      if (!state.rule) return dcI18n.headerNotAvailable;
      if (state.rule.nextDay) return dcI18n.headerDeliveryTomorrow;
      if (state.period === 'after') return configuredStartText(state.rule, true);
      if (state.period === 'before') return configuredStartText(state.rule, false);
      return state.rule.fast ? dcI18n.headerFastStart : dcI18n.headerDeliverToday;
    }

    function renderStatus(button, state) {
      var label = headerText(state);
      // The Header owns the optional merchant icon; every shared entry uses it.
      if (button.dataset.dcContext === 'cart' && triggerBtns.length) {
        var sourceIcon = triggerBtns[0].querySelector('.header-delivery-status__icon');
        var icon = button.querySelector('.header-delivery-status__icon');
        if (sourceIcon && icon && sourceIcon.innerHTML !== icon.innerHTML) icon.innerHTML = sourceIcon.innerHTML;
      }
      button.setAttribute('aria-expanded', popup.hidden ? 'false' : 'true');
        button.dataset.dcStatus = state.tier;
        button.dataset.dcZone = state.zone;
        button.dataset.dcRule = state.ruleId;
        button.classList.remove(
          'is-pending',
          'is-unknown',
          'is-same-day',
          'is-next-day',
          'is-none',
          'is-start-at-10'
        );
        button.classList.add('is-' + state.tier);
        if (state.rule && !state.rule.nextDay && state.period !== 'open') button.classList.add('is-start-at-10');

        var postcodeLabel = button.querySelector('[data-dc-postcode-label]');
        var statusLabel = button.querySelector('[data-dc-status-label]');
        if (postcodeLabel) postcodeLabel.textContent = selectedPostcode || dcI18n.headerArea;
        if (statusLabel) statusLabel.textContent = label;
        button.setAttribute(
          'aria-label',
          selectedPostcode ? dcI18n.headerDeliveryTo + ' ' + selectedPostcode + ', ' + label : label
        );
    }

    function updateHeader(state) {
      triggerBtns.forEach(function (button) { renderStatus(button, state); });
    }

    function amountText(template, amount) {
      return escapeHtml(template).replace(/(\$?)%%amount%%/g, function (_, currency) {
        return '<strong>' + currency + escapeHtml(amount) + '</strong>';
      });
    }

    function countdownText(seconds) {
      var time =
        '<strong>' +
        Math.floor(seconds / 3600) +
        'h ' +
        Math.floor((seconds % 3600) / 60) +
        'm ' +
        (seconds % 60) +
        's</strong>';
      return escapeHtml(dcI18n.countdown).replace(/%%time%%/g, time);
    }

    function render(state) {
      if (!selectedPostcode || invalidResult) return;

      var badge =
        state.rule && state.rule.nextDay
          ? dcI18n.fixedNextDayBadge
          : state.tier === 'same-day'
          ? dcI18n.sameDayBadge
          : state.tier === 'next-day'
          ? dcI18n.nextDayBadge
          : dcI18n.noneBadge;
      var message =
        state.rule && state.rule.nextDay
          ? dcI18n.fixedNextDayMsg
          : state.tier === 'same-day'
          ? dcI18n.sameDayMsg
          : state.tier === 'next-day'
          ? dcI18n.nextDayMsg
          : dcI18n.noneMsg;
      var html =
        '<div class="dc-results__tier dc-results__tier--' +
        state.tier +
        '"><span class="dc-results__badge">' +
        escapeHtml(badge) +
        '</span><span>' +
        escapeHtml(message.replace(/%%postcode%%/g, selectedPostcode)) +
        '</span></div>';

      if (state.remaining) {
        html += '<div class="dc-results__countdown">' + countdownText(state.remaining) + '</div>';
      }

      if (state.rule) {
        var checkIcon =
          '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';
        html += '<div class="dc-results__thresholds">';
        if (state.rule.fee !== null) {
          html += '<div class="dc-results__threshold-item">' + checkIcon + '<span>' +
            amountText(dcI18n.deliveryFee, state.rule.fee.toFixed(2)) + '</span></div>';
        }
        html +=
          '<div class="dc-results__threshold-item">' +
          checkIcon +
          '<span>' +
          amountText(state.rule.nextDay ? dcI18n.minimumOrderThreshold : dcI18n.sameDayThreshold, state.rule.min) +
          '</span></div>';
        if (state.rule.free) {
          html +=
            '<div class="dc-results__threshold-item">' +
            checkIcon +
            '<span>' +
            amountText(dcI18n.freeShippingThreshold, state.rule.free) +
            '</span></div>';
        }
        html += '</div>';
      }

      resultsEl.innerHTML = html;
      resultsEl.hidden = false;
    }

    function refresh(force) {
      var state = currentState();
      var key = selectedPostcode + ':' + state.zone + ':' + state.period;

      if (force || key !== lastRenderKey) {
        updateHeader(state);
        render(state);
        lastRenderKey = key;
      } else if (!popup.hidden && !invalidResult && state.remaining) {
        var countdownEl = resultsEl.querySelector('.dc-results__countdown');
        if (countdownEl) countdownEl.innerHTML = countdownText(state.remaining);
      }

      publish(state);
      return state;
    }

    function setPostcode(postcode) {
      var value = String(postcode || '').trim();
      if (!/^\d{4}$/.test(value)) return false;

      selectedPostcode = value;
      input.value = value;
      invalidResult = false;
      try {
        localStorage.setItem(STORAGE_KEY, value);
      } catch (error) {
        // The checker remains usable when browser storage is unavailable.
      }
      refresh(true);
      return true;
    }

    function clearPostcode() {
      selectedPostcode = '';
      input.value = '';
      invalidResult = false;
      resultsEl.innerHTML = '';
      resultsEl.hidden = true;
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // The checker remains usable when browser storage is unavailable.
      }
      refresh(true);
    }

    function setExpanded(expanded) {
      triggerBtns.forEach(function (button) {
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    var pickerContext = null;
    var swallowEscapeRelease = false;
    function openPicker(options) {
      if (!popup.hidden) return;
      options = options || {};
      pickerContext = {
        underlay: options.underlay || null,
        returnFocus: options.returnFocus || document.activeElement,
        wasInert: Boolean(options.underlay && options.underlay.inert)
      };
      // Keep the drawer visible while the single picker owns focus and input.
      if (pickerContext.underlay) pickerContext.underlay.inert = true;
      if (pickerContext.returnFocus) pickerContext.returnFocus.setAttribute('aria-expanded', 'true');
      popup.hidden = false;
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      refresh(true);
      setExpanded(true);
      // Enter the dialog without opening a mobile keyboard; input focus stays user-initiated.
      if (window.FoxTheme?.a11y) window.FoxTheme.a11y.trapFocus(popup, closeBtn);
      else closeBtn.focus({ preventScroll: true });
    }

    function closePicker() {
      var wasOpen = !popup.hidden;
      popup.hidden = true;
      setExpanded(false);
      if (!wasOpen) return;
      var context = pickerContext;
      pickerContext = null;
      if (context?.returnFocus) context.returnFocus.setAttribute('aria-expanded', 'false');
      if (context && context.underlay) context.underlay.inert = context.wasInert;
      if (window.FoxTheme?.a11y) {
        if (context?.underlay?.isConnected && context.underlay.open) {
          window.FoxTheme.a11y.trapFocus(context.underlay, context.returnFocus);
        } else {
          window.FoxTheme.a11y.removeTrapFocus(context?.returnFocus);
        }
      } else if (context?.returnFocus?.focus) context.returnFocus.focus();
      document.dispatchEvent(new CustomEvent('cozy:delivery-picker-close'));
    }

    triggerBtns.forEach(function (button) {
      listen(button, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openPicker();
      });
    });
    // Delegate auxiliary entries so section/cart re-renders never add listeners.
    listen(document, 'click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-cozy-delivery-open]');
      if (!trigger) return;
      event.preventDefault();
      var drawer = trigger.closest('cart-drawer');
      openPicker({ underlay: drawer && drawer.open ? drawer : null, returnFocus: trigger });
    });
    listen(closeBtn, 'click', closePicker);
    listen(overlay, 'click', closePicker);
    // Some theme modal styles hide the overlay child; the full-screen root
    // must still dismiss the picker when the user clicks outside its content.
    listen(popup, 'click', function (event) {
      if (event.target === popup) closePicker();
    });
    listen(document, 'keydown', function (event) {
      if (event.key !== 'Escape' || popup.hidden) return;
      swallowEscapeRelease = true;
      event.preventDefault();
      event.stopImmediatePropagation();
      closePicker();
    }, true);
    // Theme drawers close on keyup; consume the matching release so Escape
    // dismisses only the top picker, never the cart beneath it.
    listen(document, 'keyup', function (event) {
      if (event.key !== 'Escape' || !swallowEscapeRelease) return;
      swallowEscapeRelease = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    listen(checkBtn, 'click', function () {
      if (setPostcode(input.value)) return;

      invalidResult = true;
      resultsEl.innerHTML =
        '<div class="dc-results__tier dc-results__tier--none"><span class="dc-results__badge">' +
        escapeHtml(dcI18n.invalidBadge) +
        '</span><span>' +
        escapeHtml(dcI18n.invalidMsg) +
        '</span></div>';
      resultsEl.hidden = false;
    });
    listen(input, 'keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        checkBtn.click();
      }
    });
    listen(document, 'visibilitychange', function () {
      if (!document.hidden) refresh(true);
    });
    listen(window, 'focus', function () {
      refresh(true);
    });

    controller = {
      sectionId: sectionId,
      getState: function () {
        return publicState(currentState());
      },
      renderStatus: function (button) { renderStatus(button, currentState()); },
      setPostcode: setPostcode,
      clearPostcode: clearPostcode,
      openPicker: openPicker,
      closePicker: closePicker,
      destroy: function () {
        closePicker();
        clearInterval(timer);
        listeners.abort();
        popup.remove();
        if (activeController === controller) activeController = null;
      }
    };

    listen(document, 'shopify:section:unload', function (event) {
      if (event.detail && event.detail.sectionId === sectionId) controller.destroy();
    });

    try {
      var savedPostcode = localStorage.getItem(STORAGE_KEY);
      if (savedPostcode && /^\d{4}$/.test(savedPostcode)) {
        selectedPostcode = savedPostcode;
        input.value = savedPostcode;
      }
    } catch (error) {
      // The checker remains usable when browser storage is unavailable.
    }

    refresh(true);
    timer = setInterval(function () {
      refresh(false);
    }, 1000);

    return controller;
  }

  function mountAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var popups = scope.querySelectorAll('[data-cozy-delivery-root]');
    if (!popups.length && scope !== document) popups = document.querySelectorAll('[data-cozy-delivery-root]');
    if (!popups.length) return;

    if (activeController) activeController.destroy();
    activeController = createController(popups[popups.length - 1]);
  }

  if (window.CozyDelivery && window.CozyDelivery.version === VERSION) {
    window.CozyDelivery.mountAll(document);
    return;
  }

  var service = {
    version: VERSION,
    storageKey: STORAGE_KEY,
    eventName: CHANGE_EVENT,
    mountAll: mountAll,
    getState: function () {
      return activeController ? activeController.getState() : null;
    },
    renderStatus: function (button) {
      if (activeController && button) activeController.renderStatus(button);
    },
    setPostcode: function (postcode) {
      return activeController ? activeController.setPostcode(postcode) : false;
    },
    clearPostcode: function () {
      if (activeController) activeController.clearPostcode();
    },
    openPicker: function (options) {
      if (activeController) activeController.openPicker(options);
    },
    closePicker: function () {
      if (activeController) activeController.closePicker();
    },
    subscribe: function (callback) {
      function handler(event) {
        callback(event.detail);
      }
      document.addEventListener(CHANGE_EVENT, handler);
      return function () {
        document.removeEventListener(CHANGE_EVENT, handler);
      };
    }
  };

  window.CozyDelivery = service;
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  } else {
    mountAll(document);
  }
})();
